import { useEffect, useState } from "react";
import { usePapaParse } from "react-papaparse";
import Loading from "../Loading";

interface CsvViewerProps {
  url: string;
}

function formatCellValue(value: string): string {
  if (value === null || value === undefined || value.trim() === "") {
    return "";
  }

  const trimmed = value.trim();
  const num = Number(trimmed);
  if (trimmed !== "" && !isNaN(num) && isFinite(num)) {
    return num.toLocaleString();
  }

  return value;
}

export default function CsvViewer(props: CsvViewerProps) {
  const { readString } = usePapaParse();
  const [rows, setRows] = useState<string[][]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchAndParse() {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(props.url);
        if (!response.ok) {
          throw new Error(`Failed to fetch CSV (${response.status})`);
        }

        const text = await response.text();
        if (cancelled) return;

        readString(text, {
          skipEmptyLines: true,
          complete: (results) => {
            if (cancelled) return;
            setRows(results.data as string[][]);
            setLoading(false);
          },
        });
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load CSV");
        setLoading(false);
      }
    }

    fetchAndParse();
    return () => {
      cancelled = true;
    };
  }, [props.url]);

  if (loading) {
    return (
      <div style={{ height: 800 }}>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{ height: 800 }}
        className="flex items-center justify-center"
      >
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        style={{ height: 800 }}
        className="flex items-center justify-center"
      >
        <p className="text-sm text-gray-500">This CSV file is empty</p>
      </div>
    );
  }

  const headerRow = rows[0];
  const dataRows = rows.slice(1);

  return (
    <div className="relative h-full w-full">
      <div className="absolute inset-0 overflow-auto">
        <table className="border-collapse">
          <thead className="sticky top-0 z-10">
            <tr>
              {headerRow.map((cell, i) => (
                <th
                  key={i}
                  className="bg-white border-b border-r border-concrete-50 px-3 py-2 text-left text-xs font-semibold text-concrete-950 whitespace-nowrap"
                >
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataRows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className={rowIdx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}
              >
                {headerRow.map((_, colIdx) => (
                  <td
                    key={colIdx}
                    className="border-b border-r border-concrete-50 px-3 py-1.5 text-xs text-concrete-700 whitespace-nowrap"
                  >
                    {formatCellValue(row[colIdx] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
